import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { chessAPI } from '../services/api';
import { chessTournamentSocket } from '../services/socket';

export default function ChessTournamentDetails() {
  const { tournamentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [tournament, setTournament] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  
  const [seeking, setSeeking] = useState(false);
  const [timeLeftStr, setTimeLeftStr] = useState('');
  
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [showArenaChat, setShowArenaChat] = useState(false);
  const chatListRef = useRef(null);
  
  const seekInterval = useRef(null);
  const clockInterval = useRef(null);

  const loadTournament = async () => {
    try {
      const res = await chessAPI.getTournament(tournamentId);
      setTournament(res.data);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to load arena details');
    } finally {
      setLoading(false);
    }
  };

  const viewerId = Number(user?.id || 0);
  const isCreator = tournament && Number(tournament.creator_id) === viewerId;
  const isLoggedInPlayer = tournament && tournament.players?.some(p => Number(p.id) === viewerId);
  const me = tournament?.players?.find(p => Number(p.id) === viewerId);

  useEffect(() => {
    loadTournament();
    // Auto-refresh leaderboard and status every 7 seconds
    const refreshIval = setInterval(() => {
      loadTournament();
    }, 7000);
    return () => clearInterval(refreshIval);
  }, [tournamentId, tournament?.status]);

  useEffect(() => {
    if (tournament?.status === 'active' && isLoggedInPlayer && me?.arena_status === 'idle' && !seeking) {
      setSeeking(true);
    }
  }, [tournament?.status, isLoggedInPlayer, me?.arena_status, seeking]);

  useEffect(() => {
    if (!tournamentId) return;

    chessTournamentSocket.join(tournamentId);

    chessTournamentSocket.onChatHistory((data) => {
      if (String(data?.tournamentId) === String(tournamentId)) {
        setChatMessages(data.messages || []);
      }
    });

    chessTournamentSocket.onChatMessage((data) => {
      if (String(data?.tournamentId) === String(tournamentId)) {
        setChatMessages(prev => {
          const newArr = [...prev, data.message];
          return newArr.slice(-40);
        });
      }
    });

    return () => {
      chessTournamentSocket.removeListeners();
    };
  }, [tournamentId]);

  useEffect(() => {
    if (chatListRef.current) chatListRef.current.scrollTop = chatListRef.current.scrollHeight;
  }, [chatMessages]);

  const sendChatMessage = (e) => {
    if (e) e.preventDefault();
    if (!chatInput.trim() || !tournamentId) return;
    
    chessTournamentSocket.sendChat({ 
      tournamentId: String(tournamentId), 
      text: chatInput 
    }, (res) => {
      if (res?.ok) {
        setChatInput('');
      } else {
        console.error('Chat send failed:', res?.error);
      }
    });
  };

  // Tick the global clock down
  useEffect(() => {
    if (!tournament) return;
    if (tournament.status === 'completed') {
      setTimeLeftStr('Arena Concluded');
      return;
    }
    if (tournament.status === 'pending') {
      setTimeLeftStr('Waiting to Start');
      return;
    }
    if (tournament.status === 'active' && tournament.started_at) {
      if (clockInterval.current) clearInterval(clockInterval.current);
      clockInterval.current = setInterval(() => {
        const startMs = new Date(tournament.started_at).getTime();
        const durMs = Number(tournament.duration_minutes || 30) * 60 * 1000;
        const remaining = (startMs + durMs) - Date.now();
        if (remaining <= 0) {
          setTimeLeftStr('Arena Concluded');
          clearInterval(clockInterval.current);
          loadTournament();
        } else {
          const m = Math.floor(remaining / 60000);
          const s = Math.floor((remaining % 60000) / 1000);
          setTimeLeftStr(`${m}m ${s < 10 ? '0' : ''}${s}s`);
        }
      }, 1000);
    }
    return () => clearInterval(clockInterval.current);
  }, [tournament]);

  // The Seeking Hook
  useEffect(() => {
    if (seeking) {
      seekInterval.current = setInterval(async () => {
        try {
          // Fire the seek endpoint repeatedly
          const res = await chessAPI.seekArenaMatch(tournamentId);
          if (res.data.status === 'playing' || res.data.status === 'paired') {
            clearInterval(seekInterval.current);
            setSeeking(false);
            navigate(`/chess/duel/${res.data.matchId}?tournamentId=${tournamentId}`);
          }
        } catch (e) {
          // Ignored quiet fail
          console.error('Seek error', e);
        }
      }, 3000);
    } else {
      if (seekInterval.current) clearInterval(seekInterval.current);
    }
    return () => clearInterval(seekInterval.current);
  }, [seeking, tournamentId, navigate]);

  const handleStart = async () => {
    try {
      setLoading(true);
      const res = await chessAPI.startTournament(tournamentId);
      setTournament(res.data);
      setLoading(false);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to start arena');
      setLoading(false);
    }
  };

  const handleJoin = async () => {
    try {
      setLoading(true);
      const res = await chessAPI.joinTournament(tournamentId);
      setTournament(res.data);
      setLoading(false);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to join arena');
      setLoading(false);
    }
  };

  const handleSeek = () => {
    setSeeking(true);
  };
  
  const handleCancelSeek = async () => {
    setSeeking(false);
    try {
      await chessAPI.unseekArenaMatch(tournamentId);
    } catch (e) {}
  };

  const handleCancelTournament = async () => {
    if (!window.confirm('Cancel and delete this Arena?')) return;
    try {
      await chessAPI.cancelTournament(tournamentId);
      navigate('/chess/tournaments');
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to cancel arena');
    }
  };

  // Definitions moved up


  return (
    <div className="dashboard-container dashboard-redesign">
      <div className="dashboard-content">
        <div className="chess-lobby-header">
          <div className="header-left">
            <div className="header-icon">🔥</div>
            <div>
              <h1 className="page-title">{tournament?.name || 'Arena Details'}</h1>
              <p className="page-subtitle">{timeLeftStr}</p>
            </div>
          </div>
          <button 
            className="back-btn" 
            disabled={seeking}
            onClick={() => {
              navigate('/chess/tournaments');
            }}
            style={{ opacity: seeking ? 0.5 : 1, cursor: seeking ? 'not-allowed' : 'pointer' }}
          >
            ← Back to Arenas
          </button>
        </div>

        {error && (
          <div className="error-alert">
            <span className="error-icon">⚠️</span>
            <span>{error}</span>
            <button className="error-close" onClick={() => setError('')}>×</button>
          </div>
        )}

        {loading && <div style={{ color: 'white', marginTop: 24 }}>Loading Arena...</div>}

        {!loading && tournament && (
          <>
            {/* Action Bar */}
            <div className="user-stats-card" style={{ marginBottom: 24, padding: 32, textAlign: 'center', background: 'linear-gradient(135deg, rgba(225, 29, 72, 0.1), rgba(15, 23, 42, 1))' }}>
                <h2 style={{ fontSize: 32, margin: '0 0 16px 0' }}>{timeLeftStr}</h2>

                {/* Wager & Prize Info */}
                {(() => {
                  const wager = Number(tournament.entry_wager || 0);
                  const playerCount = Number(tournament.player_count || 0);
                  const grossPot = wager * playerCount;
                  if (wager > 0) {
                    return (
                      <div style={{ display: 'flex', justifyContent: 'center', gap: 24, marginBottom: 20, flexWrap: 'wrap' }}>
                        <div style={{ background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: 10, padding: '10px 20px', minWidth: 120 }}>
                          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Entry Wager</div>
                          <div style={{ fontSize: 20, fontWeight: 700, color: '#10b981' }}>{wager} wkp</div>
                        </div>
                        <div style={{ background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 10, padding: '10px 20px', minWidth: 120 }}>
                          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Prize Pot</div>
                          <div style={{ fontSize: 20, fontWeight: 700, color: '#f59e0b' }}>{grossPot} wkp</div>
                        </div>
                        <div style={{ background: 'rgba(225,29,72,0.12)', border: '1px solid rgba(225,29,72,0.3)', borderRadius: 10, padding: '10px 20px', minWidth: 120 }}>
                          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Mode</div>
                          <div style={{ fontSize: 16, fontWeight: 700, color: '#e11d48' }}>💰 REAL</div>
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div style={{ marginBottom: 20 }}>
                      <span style={{ background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.3)', borderRadius: 8, padding: '6px 16px', fontSize: 13, color: '#818cf8' }}>🎮 Friendly Arena — No entry fee</span>
                    </div>
                  );
                })()}

                {tournament.status === 'pending' && <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: 24 }}>Waiting for the creator to open the Arena.</p>}
                
                <div style={{ display: 'flex', justifyContent: 'center', gap: 16 }}>
                  {tournament.status === 'pending' && !isLoggedInPlayer && (
                    <button className="flappy-btn-primary" onClick={handleJoin} style={{ padding: '16px 32px', fontSize: 18 }}>
                      Join Arena
                    </button>
                  )}
                  {tournament.status === 'pending' && isCreator && (
                    <button className="flappy-btn-secondary" onClick={handleStart} style={{ padding: '16px 32px', fontSize: 18, background: '#e11d48', borderColor: '#be123c', color: 'white' }}>
                      Start Arena Now
                    </button>
                  )}
                  {tournament.status === 'active' && !isLoggedInPlayer && (
                    <button 
                      className="flappy-btn-primary" 
                      onClick={handleJoin} 
                      disabled={tournament.players?.length >= 32}
                      style={{ 
                        padding: '16px 32px', 
                        fontSize: 18, 
                        background: (tournament.players?.length >= 32) ? '#475569' : '#10b981', 
                        borderColor: (tournament.players?.length >= 32) ? '#334155' : '#059669',
                        cursor: (tournament.players?.length >= 32) ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {tournament.players?.length >= 32 ? 'Arena Full (32/32)' : 'Join & Play'}
                    </button>
                  )}
                  {tournament.status === 'active' && isLoggedInPlayer && !seeking && me?.arena_status !== 'playing' && (
                    <button className="flappy-btn-primary" onClick={handleSeek} style={{ padding: '16px 40px', fontSize: 20, background: '#10b981', borderColor: '#059669', animation: 'pulse 2s infinite' }}>
                      Play
                    </button>
                  )}
                  {tournament.status === 'active' && isLoggedInPlayer && seeking && (
                    <div className="flappy-btn-secondary" style={{ padding: '16px 40px', fontSize: 20, opacity: 0.8, cursor: 'default' }}>
                      <span className="seeking-dot"></span> Searching Opponent...
                    </div>
                  )}
                  {tournament.status === 'active' && isLoggedInPlayer && me?.arena_status === 'playing' && (
                    <button 
                      className="flappy-btn-primary" 
                      onClick={() => navigate(`/chess/duel/${me.active_match_id}?tournamentId=${tournamentId}`)}
                      style={{ padding: '16px 40px', fontSize: 20, background: '#f59e0b', borderColor: '#d97706', animation: 'pulse 2s infinite' }}
                    >
                      ⚔️ YOU ARE IN A MATCH - JOIN BACK
                    </button>
                  )}
                  {!isLoggedInPlayer && tournament.status === 'active' && (
                    <div style={{ padding: '16px 40px', fontSize: 18, color: 'rgba(255,255,255,0.6)' }}>
                       Arena is strictly underway.
                    </div>
                  )}
                  {isCreator && tournament.status === 'pending' && (
                    <button
                      className="flappy-btn-secondary"
                      onClick={handleCancelTournament}
                      style={{ padding: '12px 24px', fontSize: 14, background: '#7f1d1d', borderColor: '#991b1b', color: 'white', opacity: 0.8 }}
                    >
                      🗑 Cancel Arena
                    </button>
                  )}
                </div>
            </div>

            <div style={{ display: 'block' }}>
              {/* Leaderboard - Full Width Now */}
              <div className="matches-section" style={{ margin: 0 }}>
                <div className="section-header">
                  <h2 className="section-title">Live Leaderboard</h2>
                  <p className="section-subtitle">2 pts per win, 1 pt per draw. Streaks grant +4 pts!</p>
                </div>
                <div className="matches-list">
                  {tournament.players?.map((player, index) => (
                    <div key={player.id} className="match-card chess-tournament-card" style={{ display: 'flex', alignItems: 'center' }}>
                      <div style={{ paddingRight: 20, fontSize: 24, fontWeight: 800, color: index === 0 ? '#facc15' : index === 1 ? '#e2e8f0' : index === 2 ? '#b45309' : 'rgba(255,255,255,0.2)' }}>
                        #{index + 1}
                      </div>
                      <div className="match-card-main" style={{ flex: 1, padding: 0 }}>
                        <div>
                          <div className="match-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {player.username}
                            {Number(player.win_streak) > 1 && <span title={`${player.win_streak} win streak!`} style={{ fontSize: 16 }}>🔥</span>}
                          </div>
                          <div className="match-meta">
                            {player.chess_rating || 1200} ELO • {player.arena_status}
                          </div>
                        </div>
                      </div>
                      <div style={{ fontSize: 24, fontWeight: 700, paddingLeft: 16 }}>
                        {player.score} <span style={{ fontSize: 16, color: 'rgba(255,255,255,0.4)', fontWeight: 400 }}>pts</span>
                      </div>
                    </div>
                  ))}
                  {tournament.players?.length === 0 && <div style={{ color: 'rgba(255,255,255,0.6)' }}>No players have joined this arena yet.</div>}
                </div>
              </div>

              {/* Toggleable Drawer Chat */}
              <div 
                className={`cm-chat-container ${showArenaChat ? 'active' : ''}`}
                style={{
                  position: 'fixed',
                  bottom: '80px',
                  right: '24px',
                  width: '350px',
                  height: '500px',
                  background: '#1e293b',
                  borderRadius: '16px',
                  display: showArenaChat ? 'flex' : 'none',
                  flexDirection: 'column',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  zIndex: 1000,
                  overflow: 'hidden'
                }}
              >
                <div style={{ padding: '16px', background: 'rgba(0,0,0,0.4)', fontWeight: 'bold', borderBottom: '1px solid rgba(255,255,255,0.1)', display: 'flex', justifyContent: 'space-between' }}>
                  <span>Arena Chat</span>
                  <button onClick={() => setShowArenaChat(false)} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer' }}>✕</button>
                </div>
                <div ref={chatListRef} className="cm-chat-messages" style={{ flex: 1, padding: '16px', overflowY: 'auto' }}>
                  {chatMessages.map(msg => (
                    <div key={msg.id} className="cm-chat-message" style={{ marginBottom: '8px' }}>
                      <span className="cm-chat-user" style={{ fontWeight: 'bold', color: '#facc15' }}>{msg.username}: </span>
                      <span className="cm-chat-text" style={{ color: 'rgba(255,255,255,0.8)' }}>{msg.text}</span>
                    </div>
                  ))}
                  {chatMessages.length === 0 && <div style={{ color: 'rgba(255,255,255,0.4)', textAlign: 'center', marginTop: 32 }}>No messages yet...</div>}
                </div>
                <form onSubmit={sendChatMessage} className="cm-chat-form" style={{ padding: '16px', borderTop: '1px solid rgba(255,255,255,0.1)', display: 'flex', gap: 8 }}>
                  <input 
                    type="text" 
                    className="cm-input"
                    placeholder="Message the arena..." 
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    maxLength={100}
                    style={{ flex: 1, padding: '10px', borderRadius: '8px', border: 'none', background: 'rgba(0,0,0,0.2)', color: 'white' }}
                  />
                  <button 
                    type="submit" 
                    style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', background: '#e11d48', color: 'white', cursor: 'pointer' }}
                  >
                    Send
                  </button>
                </form>
              </div>

              {/* Floating Chat Icon */}
              <button
                onClick={() => setShowArenaChat(!showArenaChat)}
                style={{
                  position: 'fixed',
                  bottom: '24px',
                  right: '24px',
                  width: '56px',
                  height: '56px',
                  borderRadius: '28px',
                  background: '#e11d48',
                  color: 'white',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                  zIndex: 1001,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                💬
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
